import _ from 'lodash';
import Quill from 'quill/core';
import Header from 'quill/formats/header';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useIsSidebarOpen } from '../app/layout/IsSidebarOpenContext';
import { LAYOUT_DEBOUNCE_TIMEOUT } from '../app/layout/SidebarShiftStateProvider';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { useQuill } from '../editor/QuillContext';
import { UIActions } from '../ui/uiSlice';
import PostLayoutSizeProvider from '../utils/responsive/PostLayoutSizeProvider';
import type { PostLayoutSize } from '../utils/responsive/Sizes';
import { getCurrentPostLayoutSize } from '../utils/responsive/Sizes';
import { RIGHT_PANEL_WIDTH } from './PostLayoutSizes';
import { TYPING_DEBOUNCE_TIMEOUT } from './constants';
import usePostLayoutStateReducer from './usePostLayoutStateReducer';

type PostLayoutState = {
  isTOCExpanded: boolean;
  canTOCExpand: boolean;
  canMagneticWallExpand: boolean;
  isMagneticWallExpanded: boolean;
};

const PostLayoutStateContext = React.createContext<PostLayoutState>({
  isTOCExpanded: false,
  canTOCExpand: false,
  canMagneticWallExpand: false,
  isMagneticWallExpanded: false,
});

const ToggleTOCContext = React.createContext<(forceValue?: boolean) => void>(
  _.noop,
);

const useObservedPostLayoutSize = () => {
  const isRightPanelOpen = useAppSelector(
    (state) => state.ui.rightPanel.isOpen,
  );
  const [postLayoutSize, setPostLayoutSize] = useState<PostLayoutSize>(() =>
    getCurrentPostLayoutSize({
      accountForRightPanel: isRightPanelOpen,
      baseWidth: window.innerWidth,
    }),
  );

  useEffect(() => {
    const handleResize = () => {
      const newPostLayoutSize = getCurrentPostLayoutSize({
        accountForRightPanel: isRightPanelOpen,
        baseWidth: window.innerWidth,
      });
      setPostLayoutSize(newPostLayoutSize);
    };

    handleResize();

    const debouncedHandleResize = _.debounce(
      handleResize,
      LAYOUT_DEBOUNCE_TIMEOUT,
    );
    window.addEventListener('resize', debouncedHandleResize, { passive: true });
    return () => window.removeEventListener('resize', debouncedHandleResize);
  }, [isRightPanelOpen]);

  return postLayoutSize;
};

interface PostLayoutProviderProps {
  children: React.ReactNode;
  hasMagneticWallItems: boolean;
}

const PostLayoutProvider = ({
  children,
  hasMagneticWallItems,
}: PostLayoutProviderProps) => {
  const postLayoutSize = useObservedPostLayoutSize();

  const { quill } = useQuill();
  const isSidebarOpen = useIsSidebarOpen();
  const dispatch = useAppDispatch();

  const isRightPanelOpen = useAppSelector(
    (state) => state.ui.rightPanel.isOpen,
  );
  const magneticWallFocus = useAppSelector(
    (state) => state.editor.magneticWallFocus,
  );

  const {
    state: { expandedAreas, hasHeadings },
    dispatch: dispatchPostLayoutAction,
    canMagneticWallExpand,
    canTOCExpand,
  } = usePostLayoutStateReducer({ isSidebarOpenInitially: isSidebarOpen });

  const isMagneticWallExpanded = expandedAreas.has('magneticWall');
  const isTOCExpanded = expandedAreas.has('toc');
  const shouldOpenSidebar = expandedAreas.has('sidebar');

  // Sync back sidebar state
  useEffect(() => {
    dispatch(UIActions.toggleSidebar(shouldOpenSidebar));
  }, [dispatch, shouldOpenSidebar]);

  const toggleTOC = useCallback(
    (forceValue?: boolean) => {
      const isTOCExpandedNext = forceValue ?? !isTOCExpanded;

      dispatchPostLayoutAction({
        type: 'TOGGLE_AREA',
        payload: {
          area: 'toc',
          isExpanded: isTOCExpandedNext,
        },
      });
    },
    [isTOCExpanded, dispatchPostLayoutAction],
  );

  useEffect(() => {
    if (magneticWallFocus?.hasExpandIntent) {
      dispatchPostLayoutAction({
        type: 'TOGGLE_AREA',
        payload: {
          area: 'magneticWall',
          isExpanded: true,
        },
      });
    }
  }, [dispatchPostLayoutAction, magneticWallFocus?.hasExpandIntent]);

  useEffect(() => {
    dispatchPostLayoutAction({
      type: 'TOGGLE_AREA',
      payload: {
        area: 'sidebar',
        isExpanded: isSidebarOpen,
      },
    });
  }, [dispatchPostLayoutAction, isSidebarOpen]);

  useEffect(() => {
    dispatchPostLayoutAction({
      type: 'SET_HAS_MAGNETIC_WALL_ITEMS',
      payload: hasMagneticWallItems,
    });
  }, [dispatchPostLayoutAction, hasMagneticWallItems]);

  const debounceUpdateHasHeadings = useDebouncedCallback(() => {
    if (!quill) return;

    const hasHeadingsNext = quill.scroll.descendants(Header).length > 0;

    if (!hasHeadings && hasHeadingsNext) {
      dispatchPostLayoutAction({
        type: 'SET_HAS_HEADINGS',
        payload: true,
      });
      toggleTOC(true);
    } else if (hasHeadings && !hasHeadingsNext) {
      dispatchPostLayoutAction({
        type: 'SET_HAS_HEADINGS',
        payload: false,
      });
      toggleTOC(false);
    }
  }, TYPING_DEBOUNCE_TIMEOUT);

  useEffect(() => {
    if (!quill) return undefined;

    dispatchPostLayoutAction({
      type: 'SET_HAS_HEADINGS',
      payload: quill.scroll.descendants(Header).length > 0,
    });

    const onTextChange = () => debounceUpdateHasHeadings();

    quill.on(Quill.events.TEXT_CHANGE, onTextChange);

    return () => {
      quill.off(Quill.events.TEXT_CHANGE, onTextChange);
      debounceUpdateHasHeadings.cancel();
    };
  }, [quill, toggleTOC, debounceUpdateHasHeadings, dispatchPostLayoutAction]);

  useEffect(() => {
    const handleResize = () => {
      const currentWidth =
        window.innerWidth - (isRightPanelOpen ? RIGHT_PANEL_WIDTH : 0);
      dispatchPostLayoutAction({ type: 'RESIZE', payload: currentWidth });
    };

    handleResize();

    const debouncedHandleResize = _.debounce(
      handleResize,
      LAYOUT_DEBOUNCE_TIMEOUT,
    );

    window.addEventListener('resize', debouncedHandleResize, { passive: true });
    return () => {
      debouncedHandleResize.cancel();
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, [dispatchPostLayoutAction, isRightPanelOpen]);

  const postLayoutState = useMemo<PostLayoutState>(() => {
    return {
      isTOCExpanded,
      canTOCExpand,
      canMagneticWallExpand,
      isMagneticWallExpanded,
    };
  }, [
    canTOCExpand,
    isTOCExpanded,
    canMagneticWallExpand,
    isMagneticWallExpanded,
  ]);

  return (
    <ToggleTOCContext.Provider value={toggleTOC}>
      <PostLayoutStateContext.Provider value={postLayoutState}>
        <PostLayoutSizeProvider postLayoutSize={postLayoutSize}>
          {children}
        </PostLayoutSizeProvider>
      </PostLayoutStateContext.Provider>
    </ToggleTOCContext.Provider>
  );
};

export const usePostLayoutState = () => useContext(PostLayoutStateContext);
export const useToggleTOC = () => useContext(ToggleTOCContext);

export default PostLayoutProvider;
